/**
 * Edit Template Value Modal
 * Modal dialog for editing a template config value
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { BglPrimarySmallButton, BglSecondarySmallButton } from 'components/button/BglButtons';

interface EditTemplateValueModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (value: string) => Promise<void>;
  configKey: string;
  displayName: string;
  currentValue: string | null;
  defaultValue: string | null;
  configDataType: string;
}

export const EditTemplateValueModal: React.FC<EditTemplateValueModalProps> = ({
  open,
  onClose,
  onSave,
  configKey,
  displayName,
  currentValue,
  defaultValue,
  configDataType,
}) => {
  const [value, setValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue(currentValue || '');
      setError(null);
    }
  }, [open, currentValue]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await onSave(value);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save value');
    } finally {
      setIsSaving(false);
    }
  };

  const isJSON = configDataType === 'JSON';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Template Value
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {displayName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Config Key: <code>{configKey}</code>
          </Typography>
          
          {defaultValue && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="caption">
                <strong>Default Value:</strong> {defaultValue}
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            minRows={isJSON ? 6 : 3}
            maxRows={20}
            label="Template Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={defaultValue || 'Enter value...'}
            sx={{
              fontFamily: isJSON ? 'monospace' : 'inherit',
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <BglSecondarySmallButton onClick={onClose} disabled={isSaving}>
          Cancel
        </BglSecondarySmallButton>
        <BglPrimarySmallButton onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </BglPrimarySmallButton>
      </DialogActions>
    </Dialog>
  );
};

